import { FileDownload } from '@mui/icons-material';
import { IconButton, ListSubheader, Menu } from '@mui/material';
import {
  GridExcelExportOptions,
  gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import moment from 'moment';
import { MouseEvent, MutableRefObject, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getExportFileName } from '~/utils/helper';

import {
  ExportOptionMenuItem,
  ExportOptionMenuItemProps,
} from './ExportOptionMenuItem';

export enum ReportExportOptions {
  Excel = 'excel',
  CSV = 'csv',
  PDF = 'pdf',
}

type PropsType = {
  title: string;
  subtitle?: string;
  startDate?: Date;
  endDate?: Date;
  apiRef: MutableRefObject<GridApiPremium | null>;
  exportOptions?: ReportExportOptions[];
  isLoading?: boolean;
};

export const ReportDataExport = (props: PropsType) => {
  const {
    title,
    subtitle,
    apiRef,
    startDate,
    endDate,
    exportOptions: exportOptionsConfig,
    isLoading,
  } = props;

  const { t } = useTranslation();

  const { menuOptions, exportOptions, documentDetails } = useMemo(() => {
    const defaultExportOptions = [
      ReportExportOptions.Excel,
      ReportExportOptions.CSV,
      ReportExportOptions.PDF,
    ];

    const exportOptions = !!exportOptionsConfig
      ? exportOptionsConfig
      : defaultExportOptions;

    const resolveDateRange = () => {
      const formatDate = (date: Date) => moment(date).format('L');

      if (!!startDate && !!endDate) {
        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
      } else if (!!startDate) {
        return formatDate(startDate);
      }

      return undefined;
    };

    const documentDetails = {
      title,
      subtitle: subtitle ?? resolveDateRange(),
    };
    const fileName = getExportFileName({
      title,
      startDate,
      endDate,
    });

    const menuOptions: ExportOptionMenuItemProps[] = [
      {
        type: ReportExportOptions.Excel,
        label: t('laExpExcel'),
        options: {
          fileName,
        },
      },
      {
        type: ReportExportOptions.CSV,
        label: t('laExpCSV'),
        options: {
          fileName,
          delimiter: ';',
          utf8WithBom: true,
        },
      },
      {
        type: ReportExportOptions.PDF,
        label: t('laExpPDF'),
        options: {
          fileName,
        },
      },
    ].filter(({ type }) => exportOptions.includes(type));

    return {
      menuOptions,
      exportOptions,
      documentDetails,
    };
  }, [title, subtitle, startDate, endDate, t, exportOptionsConfig]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const selectedRowsCount = !!apiRef?.current?.getSelectedRows
    ? apiRef?.current?.getSelectedRows()?.size || 0
    : 0;
  const hasSelectedRows = selectedRowsCount > 0;

  const filteredRowsCount = !!apiRef?.current?.state
    ? gridFilteredSortedRowIdsSelector(apiRef)?.length
    : 0;

  return (
    <>
      <IconButton
        id={'report-export-button'}
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup={'true'}
        aria-expanded={open ? 'true' : undefined}
        disabled={exportOptions.length < 1 || isLoading}
        onClick={handleClick}
      >
        <FileDownload />
      </IconButton>
      <Menu
        id={'report-export-menu'}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'report-export-button',
          },
        }}
      >
        {hasSelectedRows && (
          <ListSubheader sx={{ lineHeight: '2em' }}>{`${t(
            'selected',
          )} (${selectedRowsCount})`}</ListSubheader>
        )}
        {menuOptions.map((exportProps, index) => {
          return (
            <ExportOptionMenuItem
              apiRef={apiRef}
              {...exportProps}
              key={`export-option-${index}`}
              documentDetails={documentDetails}
            />
          );
        })}
        {hasSelectedRows && (
          <ListSubheader sx={{ lineHeight: '2em' }}>{`${t(
            'all',
          )} (${filteredRowsCount})`}</ListSubheader>
        )}
        {hasSelectedRows &&
          menuOptions.map((exportProps, index) => {
            const exportOptions: GridExcelExportOptions = {
              ...exportProps.options,
              getRowsToExport: (params) => {
                return gridFilteredSortedRowIdsSelector(params.apiRef);
              },
            };

            return (
              <ExportOptionMenuItem
                key={`export-all-option-${index}`}
                apiRef={apiRef}
                {...exportProps}
                options={exportOptions}
                documentDetails={documentDetails}
              />
            );
          })}
      </Menu>
    </>
  );
};
